"use client";

import { formatMonthYear } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";

// Query params that drive paging/sorting rather than filtering.
const IGNORED_PARAMS = new Set(["page", "per_page", "sort"]);

// Filter params, mapped to their short chip label. The period (month/year) is
// handled separately so the two params collapse into a single chip.
const FILTER_LABEL_KEYS: Record<string, string> = {
  search_condition: "search-condition",
  branch: "branch",
  board: "board",
  department: "department",
  departments: "department",
  production_unit: "production-unit",
  production_units: "production-unit",
  status: "status",
  validity_status: "validity-status",
  report: "report",
  report_type: "report-type",
  report_view_type: "report-view-type",
  payroll_type: "payroll-type",
  payroll_items: "payroll-item",
  parameter: "parameter",
  parameter_filters: "advanced-filter",
  calculation_rule: "calculation-rule",
  salary_rule: "calculation-rule",
  business_entity_type_id: "business-entity-type",
  scale_id: "salary-scale",
};

const KEYWORD_MAX_LENGTH = 24;

/**
 * Describes the filters currently applied to the page, read from the URL so it
 * stays correct even while the panel is collapsed and its fields are unmounted.
 */
export function useActiveFilterChips(): string[] {
  const t = useTranslations("feature.common.filters");
  const searchParams = useSearchParams();

  return useMemo(() => {
    const chips: string[] = [];
    const seen = new Set<string>();

    const month = searchParams.get("month");
    const year = searchParams.get("year");
    const yearMonths = searchParams.get("yms");

    if (yearMonths) {
      const count = yearMonths.split(",").filter(Boolean).length;
      chips.push(`${t("period")}: ${count}`);
    } else if (month && year) {
      chips.push(`${t("period")}: ${formatMonthYear(Number(month), Number(year))}`);
    }

    searchParams.forEach((value, key) => {
      if (!value || IGNORED_PARAMS.has(key)) {
        return;
      }

      const labelKey = FILTER_LABEL_KEYS[key];

      if (!labelKey || seen.has(labelKey)) {
        return;
      }

      seen.add(labelKey);

      if (key === "search_condition") {
        const keyword =
          value.length > KEYWORD_MAX_LENGTH
            ? `${value.slice(0, KEYWORD_MAX_LENGTH)}…`
            : value;
        chips.push(`${t("search-condition")}: ${keyword}`);
        return;
      }

      chips.push(t(labelKey));
    });

    return chips;
  }, [searchParams, t]);
}

const MAX_VISIBLE_CHIPS = 4;

export function SearchFilterSummary() {
  const t = useTranslations("feature.common.filters");
  const chips = useActiveFilterChips();

  if (chips.length === 0) {
    return null;
  }

  const visible = chips.slice(0, MAX_VISIBLE_CHIPS);
  const hidden = chips.length - visible.length;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {visible.map((chip) => (
        <span
          key={chip}
          className="max-w-[220px] truncate rounded-full border border-primaryAlt/20 bg-primaryAlt/10 px-2.5 py-0.5 text-xs font-medium text-primaryAlt"
        >
          {chip}
        </span>
      ))}
      {hidden > 0 && (
        <span className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
          {t("more", { count: hidden })}
        </span>
      )}
    </div>
  );
}
